import { Box, Button, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useContext, useEffect, useState } from 'react';
import 'react-phone-input-2/lib/style.css';

import { Spinner } from '@/components/common/Spinner';
import { LocaleContext } from '@/Contexts/LocaleContext';
import { SelectedProgramContext } from '@/Contexts/SelectedProgramContext';
import { SnackbarContext } from '@/Contexts/SnackbarProvider';
import { UsersContext } from '@/Contexts/UsersContext';
import useContact from '@/hooks/useContact';
import { ContactProps } from '@/models/Contact';
import { ProgramNames } from '@/models/Program';
import { USCountryId } from '@/utils/constants';
import { phoneNumberIsValid } from '@/utils/contactUtils';
import { Email } from '@/components/common/text_input/Email';
import { isEmail } from '@/utils/emailUtil';

export function ContactInfo() {
  const { currentContact, updateCurrentContact } = useContext(UsersContext);
  const { selectedProgramId } = useContext(SelectedProgramContext);

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const { saveContact } = useContact();
  const { translatedText } = useContext(LocaleContext);

  const { setAlert } = useContext(SnackbarContext);

  const [formValues, setFormValues] = useState<ContactProps>({
    id: currentContact?.id ?? 0,
    firstName: currentContact?.firstName ?? '',
    lastName: currentContact?.lastName ?? '',
    email: currentContact?.email ?? '',
    streetAddress1: currentContact?.streetAddress1 ?? '',
    streetAddress2: currentContact?.streetAddress2 ?? '',
    countyId: currentContact?.countryId == USCountryId ? (currentContact.countyId ?? '') : '999',
    stateId: currentContact?.countryId == USCountryId ? (currentContact.stateId ?? '') : '99',
    phone: currentContact?.phone ?? '',
    city: currentContact?.city ?? '',
    zipCode: currentContact?.zipCode ?? '',
    countryId: currentContact?.countryId ?? USCountryId,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (data: ContactProps) => {
    const newErrors: Record<string, string> = {};

    if (data.firstName == null || data.firstName === '' || data.firstName.length < 2) {
      newErrors.firstName = 'First name needs at least 2 characters';
    }
    if (data.lastName == null || data.lastName === '' || data.lastName.length < 2) {
      newErrors.lastName = 'Last name needs at least 2 characters';
    }
    if (data.phone == null || data.phone === '') {
      newErrors.phone = 'Phone number is required';
    } else if (!phoneNumberIsValid(data.phone)) {
      newErrors.phone = 'Please Enter a valid phone number';
    }
    if (data.email == null || data.email === '' || !isEmail(data.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (
      data.streetAddress1 == null ||
      data.streetAddress1 === '' ||
      data.streetAddress1.length < 2
    ) {
      newErrors.streetAddress1 = 'Street address is required';
    }
    if (data.city == null || data.city === '') {
      newErrors.city = 'Please Enter a City';
    }
    if (data.countyId == null || data.countyId === '' || data.countyId.length < 2) {
      newErrors.countyId = 'Select a County';
    }
    if (data.stateId == null || data.stateId === '') {
      newErrors.stateId = 'State is required';
    }
    if (data.zipCode == null || data.zipCode === '') {
      newErrors.zipCode = 'Zip Code is required';
    } else if (data.countryId == USCountryId) {
      if (data.zipCode.length != 5 || !/^\d+$/.test(data.zipCode)) {
        newErrors.zipCode = 'Zip Code must be 5 digits long';
      }
    } else if (data.zipCode.length > 10) {
      newErrors.zipCode = 'Postal Code must be less than 10 digits long';
    }

    return newErrors;
  };

  const handleFieldChange = (field: keyof ContactProps, value: string) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleSubmit = async () => {
    const newErrors = validateForm(formValues);
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    await onSave(formValues);
  };

  useEffect(() => {
    if (currentContact != null) {
      setFormValues({
        id: currentContact?.id ?? 0,
        firstName: currentContact?.firstName ?? '',
        lastName: currentContact?.lastName ?? '',
        email: currentContact?.email ?? '',
        streetAddress1: currentContact?.streetAddress1 ?? '',
        streetAddress2: currentContact?.streetAddress2 ?? '',
        countyId:
          currentContact?.countryId == USCountryId ? (currentContact.countyId ?? '') : '999',
        stateId: currentContact?.countryId == USCountryId ? (currentContact.stateId ?? '') : '99',
        phone: currentContact?.phone ?? '',
        city: currentContact?.city ?? '',
        zipCode: currentContact?.zipCode ?? '',
        countryId: currentContact?.countryId ?? USCountryId,
      });

      setLoading(false);
    }
  }, [currentContact]);

  async function onSave(values: ContactProps) {
    setSaving(true);
    try {
      const result = await saveContact(values);

      // check if result is null or a string
      if (result == null) {
        setAlert({
          severity: 'error',
          message: 'Error saving - please try again',
        });
        return;
      }

      updateCurrentContact(result);

      setAlert({
        severity: 'success',
        message: 'Contact information saved successfully',
      });
    } catch {
      setAlert({
        severity: 'error',
        message: 'Error saving - please try again',
      });
    }

    setSaving(false);
  }

  const submitDisabled =
    saving ||
    Object.values(errors).some((error) => error !== '') ||
    (selectedProgramId != ProgramNames.TreeCitiesWorld && formValues.countyId == 'x');

  return loading ? (
    <Spinner />
  ) : (
    <Box display='flex' flexDirection='column' alignItems='center'>
      <Typography variant='h2' sx={{ marginBottom: 4 }}>
        My Contact Information
      </Typography>

      <Box sx={{ width: '85%' }}>
        <Grid container rowSpacing={2} columnSpacing={4}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              id={'firstName'}
              label={translatedText.contact.labelFirstName}
              value={formValues.firstName}
              onChange={(e) => handleFieldChange('firstName', e.target.value)}
              error={errors.firstName != null && errors.firstName !== ''}
              helperText={errors.firstName}
              required
              inputProps={{ maxLength: 500 }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              id={'lastName'}
              label={translatedText.contact.labelLastNameLabel}
              value={formValues.lastName}
              onChange={(e) => handleFieldChange('lastName', e.target.value)}
              error={errors.lastName != null && errors.lastName !== ''}
              helperText={errors.lastName}
              required
              inputProps={{ maxLength: 500 }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              id={'phone'}
              label={translatedText.contact.labelPhoneNumber}
              value={formValues.phone}
              onChange={(e) => handleFieldChange('phone', e.target.value)}
              error={errors.phone != null && errors.phone !== ''}
              helperText={errors.phone}
              required
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Email
              fullWidth
              id={'email'}
              label={translatedText.contact.labelEmail}
              value={formValues.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              disabled
              required
            />
          </Grid>
        </Grid>

        <Box mt={2}>
          <TextField
            fullWidth
            id={'streetAddress1'}
            label='Street Address'
            value={formValues.streetAddress1}
            onChange={(e) => handleFieldChange('streetAddress1', e.target.value)}
            error={errors.streetAddress1 != null && errors.streetAddress1 !== ''}
            helperText={errors.streetAddress1}
            required
          />
          <TextField
            fullWidth
            id={'city'}
            label='City'
            value={formValues.city}
            onChange={(e) => handleFieldChange('city', e.target.value)}
            error={errors.city != null && errors.city !== ''}
            helperText={errors.city}
            required
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            id={'zipCode'}
            label='Zip/Postal Code'
            value={formValues.zipCode}
            onChange={(e) => handleFieldChange('zipCode', e.target.value)}
            error={errors.zipCode != null && errors.zipCode !== ''}
            helperText={errors.zipCode}
            sx={{ mt: 2 }}
          />
        </Box>

        <Box textAlign={'center'} mt={4}>
          <Button size='large' variant='primary' disabled={submitDisabled} onClick={handleSubmit}>
            {translatedText.common.buttonSave}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
